/**
 * ImportReviewScreen.jsx
 *
 * Full-screen overlay shown after resume parse succeeds.
 * Uses CSS variables so it adapts to both light and dark themes.
 */
import React from 'react';

function CountChip({ label, count, icon }) {
  if (!count || count === 0) return null;
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '10px 14px',
      borderRadius: 10,
      background: 'var(--surface-2)',
      border: '1px solid var(--card-border)',
    }}>
      <span style={{ fontSize: 16 }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>{label}</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {count} {count === 1 ? 'entry' : 'entries'} extracted
        </div>
      </div>
      <div style={{
        width: 20, height: 20, borderRadius: '50%',
        background: 'rgba(34,197,94,0.12)',
        border: '1px solid rgba(34,197,94,0.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, color: '#22c55e',
      }}>
        ✓
      </div>
    </div>
  );
}

export default function ImportReviewScreen({ parsed, onAccept, onEdit, onDiscard }) {
  if (!parsed) return null;

  const expCount   = Array.isArray(parsed.experience)     ? parsed.experience.length     : 0;
  const skillCount = Array.isArray(parsed.skills)         ? parsed.skills.length         : 0;
  const projCount  = Array.isArray(parsed.projects)       ? parsed.projects.length       : 0;
  const eduCount   = Array.isArray(parsed.education)      ? parsed.education.length      :
                     (parsed.education ? 1 : 0);
  const certCount  = Array.isArray(parsed.certifications) ? parsed.certifications.length : 0;
  const langCount  = Array.isArray(parsed.languages)      ? parsed.languages.length      : 0;
  const achCount   = Array.isArray(parsed.achievements)   ? parsed.achievements.length   : 0;

  const totalSections = [expCount, skillCount, projCount, eduCount, certCount, langCount, achCount]
    .filter(n => n > 0).length;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9991,
      background: 'rgba(0,0,0,0.55)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'Inter', sans-serif",
      padding: 24,
    }}>
      <div style={{
        background: 'var(--surface-1)',
        border: '1px solid var(--card-border)',
        borderRadius: 20,
        padding: '36px 40px',
        width: '100%',
        maxWidth: 480,
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 32px 80px rgba(0,0,0,0.25)',
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        animation: 'irs-pop 0.28s cubic-bezier(0.16,1,0.3,1)',
      }}>

        {/* Success header */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center' }}>
          <div style={{
            width: 52, height: 52, borderRadius: '50%',
            background: 'rgba(34,197,94,0.12)',
            border: '1px solid rgba(34,197,94,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22,
          }}>
            ✅
          </div>
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px' }}>
              Resume Parsed Successfully
            </h2>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
              We found {totalSections} sections. Review before continuing.
            </p>
          </div>
        </div>

        {/* Identity card */}
        {(parsed.name || parsed.email) && (
          <div style={{
            padding: '14px 16px',
            borderRadius: 12,
            background: 'rgba(59,130,246,0.06)',
            border: '1px solid rgba(59,130,246,0.15)',
          }}>
            {parsed.name && (
              <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 2 }}>
                {parsed.name}
              </div>
            )}
            {parsed.role && (
              <div style={{ fontSize: 11.5, color: '#60a5fa', marginBottom: 4 }}>
                {parsed.role}
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
              {parsed.email && (
                <span style={{ fontSize: 11, color: 'var(--text-muted)', background: 'var(--surface-2)', padding: '2px 8px', borderRadius: 6 }}>
                  {parsed.email}
                </span>
              )}
              {parsed.phone && (
                <span style={{ fontSize: 11, color: 'var(--text-muted)', background: 'var(--surface-2)', padding: '2px 8px', borderRadius: 6 }}>
                  {parsed.phone}
                </span>
              )}
              {parsed.city && (
                <span style={{ fontSize: 11, color: 'var(--text-muted)', background: 'var(--surface-2)', padding: '2px 8px', borderRadius: 6 }}>
                  {parsed.city}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Section counts */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>
            Extracted Content
          </p>
          <CountChip label="Work Experience" count={expCount}   icon="💼" />
          <CountChip label="Skills"          count={skillCount} icon="⚡" />
          <CountChip label="Projects"        count={projCount}  icon="📁" />
          <CountChip label="Education"       count={eduCount}   icon="🎓" />
          <CountChip label="Certifications"  count={certCount}  icon="🏅" />
          <CountChip label="Languages"       count={langCount}  icon="🌐" />
          <CountChip label="Achievements"    count={achCount}   icon="🏆" />
          {totalSections === 0 && (
            <div style={{
              padding: '14px', borderRadius: 10, textAlign: 'center',
              background: 'rgba(245,158,11,0.08)',
              border: '1px solid rgba(245,158,11,0.2)',
              fontSize: 12, color: '#f59e0b',
            }}>
              ⚠️ Limited data extracted. You can fill in details manually in the editor.
            </div>
          )}
        </div>

        {/* CTAs */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 4 }}>
          <button
            onClick={onAccept}
            style={{
              width: '100%', padding: '13px 0', borderRadius: 12,
              background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
              border: 'none', color: '#fff',
              fontSize: 13.5, fontWeight: 700, cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(59,130,246,0.3)',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.01)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            ✓ Looks good — Open AI Chat
          </button>
          <button
            onClick={onEdit}
            style={{
              width: '100%', padding: '11px 0', borderRadius: 12,
              background: 'var(--surface-2)',
              border: '1px solid var(--card-border)',
              color: 'var(--text-secondary)',
              fontSize: 13, fontWeight: 500, cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            ✏️ Edit before importing
          </button>
          <button
            onClick={onDiscard}
            style={{
              width: '100%', padding: '9px 0', borderRadius: 10,
              background: 'transparent', border: 'none',
              color: 'var(--text-muted)',
              fontSize: 12, cursor: 'pointer',
              transition: 'color 0.15s',
            }}
          >
            Discard import
          </button>
        </div>
      </div>

      <style>{`
        @keyframes irs-pop {
          from { opacity: 0; transform: scale(0.94) translateY(12px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
